/**
 * Synchronously-hydrated, schema-validated persisted atoms — the effect-atom
 * replacement for zustand `persist` stores (see
 * docs/projects/complete/web/zustand-to-effect-atom-migration.md).
 *
 * Why not `Atom.kvs`: the v3 `Atom.kvs` hydrates asynchronously (the atom
 * returns `defaultValue` until the KeyValueStore read resolves), which
 * flashes defaults for preference stores. Both of our backends (web
 * localStorage, mobile MMKV) are synchronous, so this helper reads at atom
 * build time instead. Effect v4's `Atom.kvs({ mode: 'sync' })` has the same
 * shape — when the repo moves to v4 this helper deletes into it.
 *
 * Storage is a boot-time slot: web uses the localStorage default; mobile
 * calls `setAtomKvsStorage` (via `wireAtomKvsToMmkv`) at module load in
 * `_layout.tsx`, before any atom is first read. During SSR there is no storage, so atoms
 * build with their default value; the client rebuilds from real storage.
 *
 * SSR: every atom carries `Atom.withServerValue(defaultValue)`, which
 * `useAtom`/`useAtomValue` feed to React's `getServerSnapshot` — so the
 * server render AND the client's hydration render both see `defaultValue()`,
 * and React swaps in the stored value on the post-hydration re-render. No
 * hydration mismatch; the cost is one default-painted frame on full page
 * loads. Consumers must DERIVE from the atom value each render, never copy
 * it into a `useState` initializer — the initializer would latch the
 * hydration-render default and go stale when the stored value lands.
 * (React Native never hydrates and client-only components mount post-
 * hydration, so both read storage directly on first render, as before.)
 */
import { Atom } from 'effect/reactivity';
import { Option, Schema } from 'effect';

export type AtomKvsStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

const noopStorage: AtomKvsStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

// Even touching `localStorage` can throw (SecurityError in storage-blocked
// iframes), and Safari private mode can throw on setItem — a persisted
// preference must never crash the app, so every storage access fails open.
const detectDefaultStorage = (): AtomKvsStorage => {
  try {
    return typeof localStorage === 'undefined' ? noopStorage : localStorage;
  } catch {
    return noopStorage;
  }
};

let atomKvsStorage: AtomKvsStorage = detectDefaultStorage();

/**
 * Re-point persisted atoms at a platform store (mobile: MMKV). Must run at
 * app boot, before the first read of any `atomKvsSync` atom — already-built
 * atoms do NOT rehydrate from the new storage.
 */
export function setAtomKvsStorage(storage: AtomKvsStorage) {
  atomKvsStorage = storage;
}

/**
 * The current boot-slot storage, for non-atom persistence that should follow
 * the same platform store (e.g. `analytics/session-telemetry`). Read lazily —
 * call at use time, not module load, so mobile's boot-time
 * `setAtomKvsStorage` is honored.
 */
export const getAtomKvsStorage = (): AtomKvsStorage => atomKvsStorage;

export type KvsSyncOptions<A, I> = {
  readonly key: string;
  readonly schema: Schema.Codec<A, I>;
  readonly defaultValue: () => A;
  /**
   * Per-atom storage override (e.g. a sessionStorage adapter). Defaults to
   * the boot-slot storage (web localStorage / mobile MMKV).
   */
  readonly storage?: AtomKvsStorage;
  /**
   * Salvage path for when the whole-blob decode fails. Receives the raw
   * stored JSON string and returns a best-effort value — see
   * {@link fieldwiseKvsFallback}, which recovers the still-valid fields of a
   * flat preference object instead of wiping them all. Omit to keep the plain
   * "fail open to `defaultValue()`" behavior.
   */
  readonly fieldFallback?: (rawJson: string) => A;
};

/**
 * The storage read that backs {@link atomKvsSync}, as a standalone closure so
 * the same persisted value can also be read from outside React — where an
 * atom needs a registry and web's `RegistryProvider` is per-React-tree, so
 * there is none to reach. Build it ONCE per key (it compiles the codec) and
 * point it at the same options object the atom uses, so the two can't drift.
 */
export function kvsSyncReader<A, I>(options: KvsSyncOptions<A, I>): () => A {
  const resolveStorage = () => options.storage ?? atomKvsStorage;
  // fromJsonString composes the JSON string codec into the schema: one decode
  // covers JSON.parse failure AND validation together. Throws on failure
  // (`Sync` variant) — wrapped below for the fail-open behavior.
  const decodeStored = Schema.decodeSync(Schema.fromJsonString(options.schema));
  return () => {
    let raw: string | null;
    try {
      raw = resolveStorage().getItem(options.key);
    } catch {
      raw = null;
    }
    if (raw === null) return options.defaultValue();
    try {
      return decodeStored(raw);
    } catch {
      return options.fieldFallback
        ? options.fieldFallback(raw)
        : options.defaultValue();
    }
  };
}

/**
 * A writable, keepAlive atom persisted under `key` with synchronous
 * hydration. A missing key or a persisted value that fails schema decode
 * yields `defaultValue()` (same fail-open behavior as zustand `persist` —
 * a corrupt preference must never crash the app); writes are encoded through
 * the schema before storage.
 */
export function atomKvsSync<A, I>(
  options: KvsSyncOptions<A, I>,
): Atom.Writable<A> {
  const resolveStorage = () => options.storage ?? atomKvsStorage;
  const encodeStored = Schema.encodeSync(Schema.fromJsonString(options.schema));
  const read = kvsSyncReader(options);

  // Fail open on schema-invalid values and throwing writes (Safari private
  // mode, quota): the in-memory atom keeps the new value; it just won't
  // survive a reload.
  const persist = (value: A): void => {
    try {
      const json = encodeStored(value);
      resolveStorage().setItem(options.key, json);
    } catch {
      // fails open — see file header comment
    }
  };

  return Atom.writable<A, A>(
    () => read(),
    (ctx, value) => {
      persist(value);
      ctx.setSelf(value);
    },
  ).pipe(
    Atom.keepAlive,
    // See the SSR section of the file header: server + hydration renders read
    // the default, the post-hydration re-render swaps in storage.
    Atom.withServerValue(() => options.defaultValue()),
  );
}

const UnknownFields = Schema.Record(Schema.String, Schema.Unknown);
const decodeStoredJson = Schema.decodeUnknownOption(
  Schema.fromJsonString(UnknownFields),
);
const decodeStateEnvelope = Schema.decodeUnknownOption(
  Schema.Struct({ state: UnknownFields }),
);

/**
 * Build a field-wise decode fallback for a flat preference struct, for
 * `atomKvsSync`'s `fieldFallback`. It runs only when the whole-blob decode
 * (the store's envelope/partial migration transform) fails — for example
 * because ONE field holds a retired enum literal after an app update. Rather
 * than wiping the entire persisted object back to defaults, it:
 *
 *   1. parses the raw JSON (unparseable ⇒ all defaults),
 *   2. unwraps the zustand `{state, version}` envelope if present (matching the
 *      store transforms), and
 *   3. decodes each TOP-LEVEL field independently against its own schema,
 *      keeping every field that still validates and defaulting only the ones
 *      that don't.
 *
 * So a single bad field costs the user only that one preference. Recovery is
 * top-level only: a bad value nested inside a field defaults that whole field
 * (the granularity these preference schemas are shaped for). Pass the BARE
 * state struct schema (not the migration transform) and its defaults.
 */
export function fieldwiseKvsFallback<
  Fields extends Schema.Struct.Fields,
  A extends Record<string, unknown>,
>(options: {
  readonly schema: Schema.Struct<Fields>;
  readonly defaultValue: () => A;
}): (rawJson: string) => A {
  const fieldEntries = Object.entries(options.schema.fields);
  return (rawJson) => {
    const defaults = options.defaultValue();
    const parsed = decodeStoredJson(rawJson);
    if (Option.isNone(parsed)) return defaults;
    const envelope = parsed.value;
    const source = decodeStateEnvelope(envelope).pipe(
      Option.match({ onNone: () => envelope, onSome: ({ state }) => state }),
    );

    const result: Record<string, unknown> = { ...defaults };
    for (const [key, field] of fieldEntries) {
      if (!(key in source)) continue;
      try {
        result[key] = Schema.decodeUnknownSync(field as Schema.Codec<unknown>)(
          source[key],
        );
      } catch {
        // keep the default already seeded into `result`
      }
    }
    return result as A;
  };
}
